import * as React from "react";

import * as moment from "moment";

import { Popup } from "semantic-ui-react";

import { debounce } from "lodash";

import { Appointment as AppointmentModel, Direction, APPOINTMENT_DATA_FORMAT } from "./models";
import { Appointment } from "./Appointment";

import { CalendarMouseEventDebounceMilliseconds } from "./Calendar";

interface Props {
    entityId?: string;
    time: moment.Moment;
    hourSize: number;
    timeDirection: Direction;
    appointments?: AppointmentModel[];
    workingDayStartTime?: string;
    workingDayEndTime?: string;
    onAppointmentClick?: (appointment: AppointmentModel) => void;
    onAppointmentDrop?: (itemId: string, appointment: AppointmentModel) => void;
    onDrop?: (itemId: string, hour: moment.Moment, entityId: string) => void;
    onClick?: (hour: moment.Moment, entityId: string) => void;
}

interface State {
    draggingOver: boolean;
}

export class Hour extends React.Component<Props, State> {
    private draggedOverDebounced = debounce(() => this.setState({ draggingOver: true }), CalendarMouseEventDebounceMilliseconds);

    constructor(props: Props) {
        super(props);
        this.state = { draggingOver: false };
        this.onDragEnter = this.onDragEnter.bind(this);
        this.onDragOver = this.onDragOver.bind(this);
        this.onDragLeave = this.onDragLeave.bind(this);
        this.onDrop = this.onDrop.bind(this);
    }

    public render() {
        const startTime = moment(this.props.workingDayStartTime, "HH:mm");
        const endTime = moment(this.props.workingDayEndTime, "HH:mm");
        const hourStart = moment(this.props.time.format("HH:mm"), "HH:mm");
        const hourEnd = moment(this.props.time.format("HH:mm"), "HH:mm").add(1, "hour");
        const workingHour = hourStart.isBetween(startTime, endTime, null, "[]") && hourEnd.isBetween(startTime, endTime, null, "[]");
        const paddingStart = endTime.isBetween(hourStart, hourEnd) ? (1 - hourEnd.diff(endTime, "hours", true)) * this.props.hourSize : 0;
        const paddingEnd = startTime.isBetween(hourStart, hourEnd) ? (1 - startTime.diff(hourStart, "hours", true)) * this.props.hourSize : 0;

        const style: React.CSSProperties = this.props.timeDirection === "vertical"
            ? { height: this.props.hourSize, paddingTop: paddingStart, paddingBottom: paddingEnd }
            : { width: this.props.hourSize, paddingLeft: paddingStart, paddingRight: paddingEnd };

        const hourElement = (
            <div
                onClick={this.onClick}
                onDrop={this.onDrop}
                onDragOver={this.onDragOver}
                onDragEnter={this.onDragEnter}
                onDragLeave={this.onDragLeave}
                className={[
                    "hour",
                    workingHour ? "" : "non-working",
                    this.state.draggingOver ? "highlighted" : "",
                    this.props.onClick ? "clickable" : ""
                ].join(" ")}
                /* eslint-disable-next-line react/forbid-dom-props */
                style={style}
            >
                {this.props.appointments && this.props.appointments.map(appointment => {
                    const { cliniciansRequired, teamPreventUsers } = appointment;
                    const issues = cliniciansRequired > teamPreventUsers.length ?
                        [`${teamPreventUsers.length} of ${cliniciansRequired} required clinicians assigned`] : [];
                    return (
                        <Appointment
                            timeDirection={this.props.timeDirection}
                            appointment={appointment}
                            issues={issues}
                            hourSize={this.props.hourSize}
                            key={appointment.start.valueOf()}
                            parentStart={this.props.time}
                            onClick={this.props.onAppointmentClick}
                            onDrop={this.props.onAppointmentDrop}
                        />);
                })}
            </div>
        );
        return (
            <Popup trigger={hourElement} open={this.state.draggingOver} content={this.props.time.format("HH:mm")} />
        );
    }

    private onDragOver(event: React.DragEvent<HTMLElement>) {
        if (this.canDropElement(event)) {
            event.preventDefault();
        }
    }

    private onDragEnter() {
        this.draggedOverDebounced();
    }

    private onDragLeave() {
        this.draggedOverDebounced.cancel();
        this.setState({ draggingOver: false });
    }

    private onDrop(event: React.DragEvent<HTMLElement>) {
        this.onDragLeave();
        if (this.canDropElement(event)) {
            const id = event.dataTransfer.getData(APPOINTMENT_DATA_FORMAT);
            this.props.onDrop(id, this.props.time, this.props.entityId);
        }
    }

    private canDropElement(event: React.DragEvent<HTMLElement>) {
        return this.props.onDrop && event.dataTransfer.types.indexOf(APPOINTMENT_DATA_FORMAT) > -1;
    }

    private onClick = () => {
        if (this.props.onClick) {
            this.props.onClick(this.props.time, this.props.entityId);
        }
    };
}
